import React, { useCallback, useEffect, useState } from "react";
import { Send, Loader2, RefreshCw, Ban, Copy, CheckCircle2, ChevronDown, ChevronUp, MessageSquare, CalendarCheck, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const StatusPill = ({ status }) => {
  const map = {
    invited: "bg-sky-100 text-sky-700",
    in_progress: "bg-amber-100 text-amber-700",
    completed: "bg-emerald-100 text-emerald-700",
    expired: "ek-surface-2 ek-text-muted",
    revoked: "bg-rose-100 text-rose-700",
  };
  return (
    <span data-testid={`sa-status-${status}`} className={`text-[11px] font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded ${map[status] || "ek-surface-2 ek-text-muted"}`}>
      {(status || "").replace("_", " ")}
    </span>
  );
};

export default function SelfAssessmentPanel() {
  const [frameworks, setFrameworks] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_email: "", mode: "fixed", framework_ids: [], expires_in_days: 5 });
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [genLoading, setGenLoading] = useState("");

  const generateReport = async (clientId) => {
    setGenLoading(clientId); toast.info("Generating Reinvention Kit…");
    try {
      const res = await fetch(`${BASE}/api/admin/enterprise360/clients/${clientId}/kit`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = m ? m[1] : "Reinvention_Kit.zip";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      toast.success("Reinvention Kit downloaded");
    } catch { toast.error("Could not generate the report"); }
    setGenLoading("");
  };

  const toggleDetail = async (ref) => {
    if (expanded === ref) { setExpanded(null); return; }
    setExpanded(ref);
    if (!details[ref]) {
      setDetailLoading(true);
      try {
        const { data } = await api.get(`/admin/self-assessments/invitations/${ref}`);
        setDetails((d) => ({ ...d, [ref]: data }));
      } catch { toast.error("Failed to load details"); }
      setDetailLoading(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fw, inv] = await Promise.all([
        api.get("/admin/assessments/frameworks"),
        api.get("/admin/self-assessments/invitations"),
      ]);
      setFrameworks(fw.data.items || []);
      setItems(inv.data.items || []);
    } catch { toast.error("Failed to load invitations"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleFw = (id) => setForm((f) => ({
    ...f,
    framework_ids: f.framework_ids.includes(id) ? f.framework_ids.filter((x) => x !== id) : [...f.framework_ids, id],
  }));

  const create = async () => {
    if (!form.client_name.trim() || !form.client_email.trim()) return toast.error("Client name and email are required");
    if (form.framework_ids.length === 0) return toast.error(form.mode === "client_choice" ? "Select at least one assessment to offer" : "Select at least one assessment");
    setSending(true);
    try {
      const { data } = await api.post("/admin/self-assessments/invitations", form);
      toast.success("Invitation sent — two emails dispatched (link + access code)");
      setForm({ client_name: "", client_email: "", mode: "fixed", framework_ids: [], expires_in_days: 5 });
      await load();
      if (data.url) navigator.clipboard?.writeText(data.url).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invitation");
    }
    setSending(false);
  };

  const reissue = async (ref) => {
    try { await api.post(`/admin/self-assessments/invitations/${ref}/reissue`); toast.success("New link + code re-sent"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reissue failed"); }
  };
  const revoke = async (ref) => {
    if (!window.confirm("Withdraw this invitation? The client's link will stop working.")) return;
    try { await api.delete(`/admin/self-assessments/invitations/${ref}`); toast.success("Invitation withdrawn"); load(); }
    catch { toast.error("Revoke failed"); }
  };

  return (
    <div data-testid="self-assessment-panel" className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold ek-text">Client self-assessment</h2>
        <p className="text-sm ek-text-muted mt-1 max-w-2xl">
          Invite a client to complete a guided assessment bundle themselves. Two emails are sent — a link and a
          separate access code. Progress saves automatically; abandoned invitations can be re-issued to resume.
          On completion you're notified and take over report generation, QA and the conversation.
        </p>
      </div>

      {/* Create invitation */}
      <div className="rounded-xl border ek-line ek-surface p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[12px] uppercase tracking-[0.12em] ek-text-muted">Client name</span>
            <input data-testid="sa-client-name" value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              className="mt-1 w-full border ek-line rounded px-3 py-2 text-sm" placeholder="Acme Global plc" />
          </label>
          <label className="block">
            <span className="text-[12px] uppercase tracking-[0.12em] ek-text-muted">Client email</span>
            <input data-testid="sa-client-email" type="email" value={form.client_email}
              onChange={(e) => setForm({ ...form, client_email: e.target.value })}
              className="mt-1 w-full border ek-line rounded px-3 py-2 text-sm" placeholder="cto@acme.com" />
          </label>
        </div>

        <div>
          <span className="text-[12px] uppercase tracking-[0.12em] ek-text-muted">Delivery mode</span>
          <div className="mt-2 flex gap-2">
            <button type="button" data-testid="sa-mode-fixed" onClick={() => setForm({ ...form, mode: "fixed" })}
              className={`flex-1 text-left border rounded-lg px-3 py-2.5 text-sm transition-colors ${form.mode === "fixed" ? "border-[#7737F6] bg-[#7737F6]/5" : "ek-line hover:border-zinc-300"}`}>
              <span className="font-medium block">I'll pre-select the bundle</span>
              <span className="text-[11px] ek-text-muted">The client completes exactly what you choose.</span>
            </button>
            <button type="button" data-testid="sa-mode-client" onClick={() => setForm({ ...form, mode: "client_choice" })}
              className={`flex-1 text-left border rounded-lg px-3 py-2.5 text-sm transition-colors ${form.mode === "client_choice" ? "border-[#7737F6] bg-[#7737F6]/5" : "ek-line hover:border-zinc-300"}`}>
              <span className="font-medium block">Let the client choose</span>
              <span className="text-[11px] ek-text-muted">They pick from the assessments you offer below.</span>
            </button>
          </div>
        </div>

        <div>
          <span className="text-[12px] uppercase tracking-[0.12em] ek-text-muted">
            {form.mode === "client_choice" ? "EKSELENS360 assessments to offer" : "EKSELENS360 assessments to include"}
          </span>
          <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {frameworks.map((f) => (
              <button key={f.id} type="button" onClick={() => toggleFw(f.id)}
                data-testid={`sa-fw-${f.sub_brand}`}
                className={`text-left border rounded px-3 py-2 text-sm transition-colors ${form.framework_ids.includes(f.id) ? "border-[#7737F6] bg-[#7737F6]/5 text-[#7737F6]" : "ek-line hover:border-zinc-300"}`}>
                <span className="font-medium">{f.sub_brand}</span>
                <span className="block text-[11px] ek-text-muted truncate">{f.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 text-[13px] ek-text-muted bg-[#F7F4FE] border border-[#e5ddff] rounded px-3 py-2">
          <span className="text-[#7737F6] font-semibold">Always included:</span> the Enterprise360 client profile (feeds the Reinvention Blueprint).
        </div>

        <div className="flex items-end justify-between gap-3 flex-wrap">
          <label className="block">
            <span className="text-[12px] uppercase tracking-[0.12em] ek-text-muted">Link expires in (days)</span>
            <input data-testid="sa-expires" type="number" min={1} max={60} value={form.expires_in_days}
              onChange={(e) => setForm({ ...form, expires_in_days: Number(e.target.value) })}
              className="mt-1 w-24 border ek-line rounded px-3 py-2 text-sm" />
          </label>
          <button data-testid="sa-send-invite" type="button" onClick={create} disabled={sending}
            className="inline-flex items-center gap-2 bg-[#7737F6] text-white px-5 py-2.5 text-[12px] tracking-[0.12em] uppercase rounded hover:bg-[#5f27d4] transition-colors disabled:opacity-40">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send invitation
          </button>
        </div>
      </div>

      {/* Invitations list */}
      <div className="rounded-xl border ek-line ek-surface">
        <div className="flex items-center justify-between px-5 py-3 border-b ek-line">
          <h3 className="text-sm font-semibold ek-text">Invitations</h3>
          <button onClick={load} className="ek-text-muted hover:text-zinc-700"><RefreshCw size={15} /></button>
        </div>
        {loading ? (
          <div className="p-6 text-center ek-text-muted"><Loader2 className="animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-sm ek-text-muted">No invitations yet.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {items.map((it) => (
              <div key={it.id} data-testid={`sa-invite-${it.id}`}>
                <div className="px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
                  <button onClick={() => toggleDetail(it.id)} data-testid={`sa-expand-${it.id}`}
                    className="min-w-[220px] text-left flex-1 group">
                    <div className="text-sm font-medium ek-text flex items-center gap-2">
                      {expanded === it.id ? <ChevronUp size={14} className="ek-text-muted" /> : <ChevronDown size={14} className="ek-text-muted" />}
                      {it.client_name} <StatusPill status={it.status} />
                      {it.booking_requested && <span className="text-[10px] font-bold text-rose-600">HOT LEAD</span>}
                    </div>
                    <div className="text-[12px] ek-text-muted ml-5">{it.client_email}</div>
                    <div className="text-[11px] ek-text-muted mt-0.5 ml-5">
                      {it.mode === "client_choice"
                        ? `Client picks from ${it.offered_framework_ids?.length || 0} offered${it.framework_ids.length ? ` · ${it.framework_ids.length} chosen` : ""}`
                        : `${it.framework_ids.length} assessment${it.framework_ids.length === 1 ? "" : "s"}`}
                      {" + E360 profile"} · expires {fmtDate(it.expires_at)}
                      {it.reminders_sent ? ` · ${it.reminders_sent} reminder${it.reminders_sent === 1 ? "" : "s"}` : ""}
                      {it.status === "completed" && it.completed_at ? ` · completed ${fmtDate(it.completed_at)}` : ""}
                    </div>
                  </button>
                  <div className="flex items-center gap-2">
                    {it.status === "completed" ? (
                      <span className="inline-flex items-center gap-1 text-[12px] text-emerald-600"><CheckCircle2 size={14} /> Ready to action</span>
                    ) : (
                      <>
                        <button data-testid={`sa-reissue-${it.id}`} onClick={() => reissue(it.id)}
                          className="inline-flex items-center gap-1 text-[11px] border ek-line px-2.5 py-1.5 rounded hover:border-[#7737F6] hover:text-[#7737F6]">
                          <RefreshCw size={12} /> Re-send
                        </button>
                        <button data-testid={`sa-revoke-${it.id}`} onClick={() => revoke(it.id)}
                          className="inline-flex items-center gap-1 text-[11px] border ek-line px-2.5 py-1.5 rounded hover:border-rose-400 hover:text-rose-600">
                          <Ban size={12} /> Withdraw
                        </button>
                      </>
                    )}
                    <button title="Copy link" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/self-assessment/${it.id}`); toast.success("Link copied"); }}
                      className="ek-text-muted hover:text-zinc-700"><Copy size={14} /></button>
                  </div>
                </div>
                {expanded === it.id && (
                  <div data-testid={`sa-detail-${it.id}`} className="px-5 pb-4 bg-zinc-50/70 border-t ek-line">
                    {detailLoading && !details[it.id] ? (
                      <div className="py-4 text-center ek-text-muted"><Loader2 size={16} className="animate-spin inline" /></div>
                    ) : details[it.id] ? (
                      <div className="pt-3 space-y-3">
                        {details[it.id].results ? (
                          <>
                            <div>
                              <div className="text-[11px] uppercase tracking-[0.12em] text-[#7737F6] font-semibold">Results summary</div>
                              <div className="mt-1.5 space-y-1.5">
                                {(details[it.id].results.frameworks || []).map((fw, i) => (
                                  <div key={i} className="text-[12.5px] ek-text-soft">
                                    <b>{fw.sub_brand}</b> — {fw.overall_level || "n/a"}{fw.overall_weighted ? ` (${fw.overall_weighted}/5)` : ""}
                                    {fw.weakest?.length > 0 && <span className="ek-text-muted"> · priority: {fw.weakest.map((w) => w.name).join(", ")}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                            {details[it.id].sales_brief && (
                              <div className="ek-surface border-l-2 border-[#7737F6] rounded px-3 py-2">
                                <div className="text-[11px] uppercase tracking-[0.12em] text-[#7737F6] font-semibold">AI sales brief</div>
                                <div className="text-[12.5px] ek-text-soft mt-1">{details[it.id].sales_brief}</div>
                              </div>
                            )}
                            <div>
                              <div className="text-[11px] uppercase tracking-[0.12em] ek-text-muted font-semibold flex items-center gap-1.5">
                                <MessageSquare size={12} /> Client questions & AI conversation ({(details[it.id].chat_transcript || []).filter((m) => m.role === "client").length})
                              </div>
                              {(details[it.id].chat_transcript || []).length === 0 ? (
                                <div className="text-[12px] ek-text-muted mt-1">No conversation yet.</div>
                              ) : (
                                <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto">
                                  {details[it.id].chat_transcript.map((m, i) => (
                                    <div key={i} className={`text-[12.5px] ${m.role === "client" ? "ek-text font-medium" : "ek-text-muted pl-3"}`}>
                                      <span className="text-[10px] uppercase ek-text-muted mr-1">{m.role === "client" ? "Q" : "AI"}</span>{m.text}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="text-[12px] flex items-center justify-between gap-3 flex-wrap">
                              <span>
                                {details[it.id].booking_requested
                                  ? <span className="inline-flex items-center gap-1 text-rose-600 font-medium"><CalendarCheck size={13} /> Session requested {fmtDate(details[it.id].booking_requested_at)}</span>
                                  : <span className="ek-text-muted">No session requested yet.</span>}
                                {details[it.id].e360_client_id && <span className="ek-text-muted ml-2">· E360 client: {details[it.id].e360_client_id}</span>}
                              </span>
                              {details[it.id].e360_client_id && (
                                <button data-testid={`sa-generate-report-${it.id}`} onClick={() => generateReport(details[it.id].e360_client_id)} disabled={genLoading === details[it.id].e360_client_id}
                                  className="inline-flex items-center gap-1.5 bg-[#7737F6] text-white px-3.5 py-2 rounded text-[11px] tracking-[0.1em] uppercase hover:bg-[#5f27d4] disabled:opacity-40">
                                  {genLoading === details[it.id].e360_client_id ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} Generate Reinvention report
                                </button>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="pt-3 text-[12.5px] ek-text-muted">Not completed yet — results and the AI conversation will appear here once the client submits.</div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
